import type { PathLookupCheck } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { showSidePanel } from 'components/Graph/commands/showSidePanel';
import { RecoverableLoader } from 'components/RecoverableLoader/RecoverableLoader';
import { ScrollPaginator } from 'components/ScrollPaginator/ScrollPaginator';
import { useLocale } from 'containers/App/LocaleContext';
import { memo } from 'react';
import type { FC } from 'react';
import { GraphPathChecksItem } from './GraphPathChecksItem';
import type { PathChecksHook } from './usePathChecks';

export const PATH_CHECK_LIST_PAGINATION_STEP = 20;

interface Props {
  onDeleteCheck: (checkId: string) => void;
  onFetchGraph: (item: PathLookupCheck) => void;
  pathChecksHook: PathChecksHook;
}

const BareGraphPathChecksList: FC<Props> = (props) => {
  const msg = useLocale();
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
  });

  const handleFetch = async () => {
    await props.pathChecksHook.fetchPathLookupChecks({
      filters: props.pathChecksHook.currentFilters,
      pagination: {
        start: items.length,
        limit: PATH_CHECK_LIST_PAGINATION_STEP,
      },
    });
  };

  const recover = () => {
    props.pathChecksHook.fetchBadge();
    handleFetch();
  };

  const addPathCheck = () => {
    ctxProps.dispatch(showSidePanel, { contentId: 'pathlookup' });
  };

  if (
    props.pathChecksHook.list.items.length === 0 &&
    props.pathChecksHook.listLoadingState === 'LOADED'
  ) {
    return (
      <div className='pt-lgu-10 flex flex-col items-center'>
        <p>{msg.canvasGraph.graphSidePanel.pathChecks.noSavedPathChecks}</p>
        <ButtonAsA
          className='graph-pathlookup-define-button font-bold underline'
          onClick={addPathCheck}
        >
          {msg.canvasGraph.graphSidePanel.pathChecks.defineNewPathCheck}
        </ButtonAsA>
      </div>
    );
  }

  const items = props.pathChecksHook.list.items.map((item) => (
    <GraphPathChecksItem
      key={item.id}
      item={item}
      onDelete={props.onDeleteCheck}
      onFetchGraph={props.onFetchGraph}
    />
  ));

  return (
    <RecoverableLoader
      errorClassName='mt-lgu-10'
      loadingState={props.pathChecksHook.listLoadingState}
      onRecover={recover}
    >
      <ScrollPaginator
        loaded={items.length}
        onFetch={handleFetch}
        total={props.pathChecksHook.list.count}
      >
        {items}
      </ScrollPaginator>
    </RecoverableLoader>
  );
};

export const GraphPathChecksList = memo(BareGraphPathChecksList);
